import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useWebphone } from "../../context/Webphone/WebphoneContext";

/**
 * Abre/fecha o widget oficial WAVoIP (carregado via CDN).
 * Igual ao código de exemplo: window.wavoip.widget.toggle()
 */
function openOfficialWebphoneWidget() {
  if (typeof window !== "undefined" && window.wavoip) {
    window.wavoip.widget.toggle();
    window.wavoip.settings.setShowSettings(false);
    setTimeout(() => {
      if (window.wavoip?.position?.set) {
        window.wavoip.position.set("bottom-right");
      }
    }, 100);
  }
}

const BUTTON_POS_KEY = "webphone_fab_position";

const useStyles = makeStyles((theme) => ({
  fab: {
    position: "fixed",
    zIndex: 9998,
    cursor: "grab",
    "&:active": {
      cursor: "grabbing",
    },
  },
  fabPositionDefault: {
    bottom: 25,
    right: 25,
    bottom: 85,
  },
  fabPositionDefaultTop: {
    top: 25,
    right: 25,
  },
  fabPositionDefaultLeft: {
    bottom: 25,
    left: 25,
    bottom: 85,
  },
  fabPositionDefaultTopLeft: {
    top: 25,
    left: 25,
  },
  button: {
    width: 45,
    height: 45,
    borderRadius: "50%",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: theme.shadows[4],
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      boxShadow: theme.shadows[8],
    },
    "&:focus": {
      outline: "none",
    },
  },
}));

/**
 * FAB arrastável no estilo do código exemplo: ao clicar abre/fecha
 * o widget WAVoIP (WavoipCall). Posição é persistida no localStorage.
 * O widget em si continua sendo o WavoipCall (wavoip-api); apenas
 * a apresentação (FAB que abre/fecha) segue o exemplo.
 */
const WebphoneFab = () => {
  const classes = useStyles();
  const { config } = useWebphone();
  const [buttonPosition, setButtonPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const didDragRef = useRef(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(BUTTON_POS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed.x === "number" && typeof parsed.y === "number") {
          setButtonPosition(parsed);
        }
      }
    } catch (_) {
      // ignore
    }
  }, []);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    const target = document.getElementById("webphone_fab");
    if (!target) return;
    const rect = target.getBoundingClientRect();
    if (!buttonPosition) {
      setButtonPosition({ x: rect.left, y: rect.top });
    }
    setIsDragging(true);
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    didDragRef.current = false;
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const target = document.getElementById("webphone_fab");
    if (!target) return;
    const maxX = window.innerWidth - target.offsetWidth;
    const maxY = window.innerHeight - target.offsetHeight;
    let newX = e.clientX - dragOffset.x;
    let newY = e.clientY - dragOffset.y;
    newX = Math.max(0, Math.min(newX, maxX));
    newY = Math.max(0, Math.min(newY, maxY));
    setButtonPosition({ x: newX, y: newY });
    didDragRef.current = true;
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (buttonPosition) {
      try {
        localStorage.setItem(BUTTON_POS_KEY, JSON.stringify(buttonPosition));
      } catch (_) {
        // ignore
      }
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, buttonPosition, dragOffset]);

  useEffect(() => {
    if (!buttonPosition) return;
    const onResize = () => {
      const target = document.getElementById("webphone_fab");
      if (!target) return;
      const maxX = window.innerWidth - target.offsetWidth;
      const maxY = window.innerHeight - target.offsetHeight;
      let { x: newX, y: newY } = buttonPosition;
      let changed = false;
      if (newX > maxX) { newX = maxX; changed = true; }
      if (newY > maxY) { newY = maxY; changed = true; }
      if (newX < 0) { newX = 0; changed = true; }
      if (newY < 0) { newY = 0; changed = true; }
      if (changed) setButtonPosition({ x: newX, y: newY });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [buttonPosition]);

  const getDefaultPositionClass = () => {
    const posY = config?.button?.position_y === "top" ? "Top" : "";
    const posX = config?.button?.position_x === "left" ? "Left" : "";
    if (posY && posX) return classes.fabPositionDefaultTopLeft;
    if (posY) return classes.fabPositionDefaultTop;
    if (posX) return classes.fabPositionDefaultLeft;
    return classes.fabPositionDefault;
  };

  const style = buttonPosition
    ? { left: buttonPosition.x, top: buttonPosition.y }
    : undefined;

  const buttonColor = config?.button?.color || "#00339E";

  if (!config?.button?.visible) return null;

  return (
    <div
      id="webphone_fab"
      className={`${classes.fab} ${!buttonPosition ? getDefaultPositionClass() : ""}`}
      style={style}
      onMouseDown={handleMouseDown}
      title="Abrir telefone (Voz Externa)"
    >
      <button
        type="button"
        className={classes.button}
        style={{ backgroundColor: buttonColor, color: "#fff" }}
        onClick={() => {
          if (didDragRef.current) {
            didDragRef.current = false;
            return;
          }
          openOfficialWebphoneWidget();
        }}
        aria-label="Abrir telefone Voz Externa"
      >
        <span style={{ fontSize: 20 }}>📞</span>
      </button>
    </div>
  );
};

export default WebphoneFab;
